/**
 * Запись голоса посетителя — замена пакета `record` из Flutter-приложения.
 *
 * Контроллер чата пользуется тремя методами: `hasPermission()` перед
 * стартом, `start()` по удержанию кнопки микрофона и `stop()`, который в
 * Dart отдавал путь к готовому файлу, а здесь — сам `File` (файловой
 * системы в браузере нет, и дальше файл всё равно уходит в multipart под
 * полем "file"). `null` из `stop()` — это `path == null` в Dart, на нём
 * контроллер бросает 'Record not completed'.
 *
 * Формат тот же, что в `RecordConfig`: WAV mono 16 кГц. `MediaRecorder`
 * такого не умеет, поэтому звук снимается сырым Float32 PCM через
 * `AudioWorkletNode` и кодируется вручную (`wavEncoder`).
 */

import {
  concatSamples,
  encodeWav,
  resampleLinear,
  WAV_MIME_TYPE,
} from '@/utils/audio/wavEncoder';
import {
  loadRecorderWorklet,
  RECORDER_WORKLET_NAME,
} from '@/utils/audio/recorderWorklet';

/** `sampleRate: 16000` из `RecordConfig` — частота, которую ждёт STT. */
export const RECORDER_SAMPLE_RATE = 16000;

/** `numChannels: 1` из `RecordConfig`. */
const RECORDER_CHANNELS = 1;

/**
 * Размер блока запасного `ScriptProcessorNode`: ~256 мс при 16 кГц.
 * Меньше — риск щелчков из-за работы в основном потоке, больше — заметная
 * задержка перед первым кадром.
 */
const SCRIPT_PROCESSOR_BUFFER_SIZE = 4096;

/** Имя ошибки getUserMedia при отказе в доступе. */
const PERMISSION_DENIED_ERROR = 'NotAllowedError';

/**
 * Ограничения потока. Эхоподавление, шумодав и автоусиление включены: во
 * Flutter их даёт сама платформа записи, без них распознавание в шумном
 * холле заметно хуже.
 */
const MEDIA_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    channelCount: RECORDER_CHANNELS,
    sampleRate: RECORDER_SAMPLE_RATE,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

type FrameHandler = (frame: Float32Array) => void;

/** Всё, что нужно освободить после остановки записи. */
interface CaptureSession {
  readonly stream: MediaStream;
  readonly context: AudioContext;
  readonly source: MediaStreamAudioSourceNode;
  readonly node: AudioNode;
}

export class AudioRecorder {
  #capture: CaptureSession | null = null;
  #frames: readonly Float32Array[] = [];
  #fileName: string | null = null;
  #disposed = false;

  /**
   * Номер попытки записи. Растёт на каждом `start()`, `stop()` и
   * `dispose()`: по нему отбрасываются кадры отменённого захвата и
   * распознаётся случай, когда посетитель отпустил кнопку раньше, чем
   * браузер открыл микрофон. В Dart ту же роль играл
   * `CancelableCompleter`.
   */
  #generation = 0;

  /**
   * Есть ли доступ к микрофону.
   *
   * Сначала Permissions API — он отвечает без окна запроса и без включения
   * устройства. `prompt`, отсутствие API или незнакомый дескриптор
   * (Firefox, Safari) означают, что спросить можно только через
   * getUserMedia.
   */
  async hasPermission(): Promise<boolean> {
    const state = await queryMicrophonePermission();
    if (state === 'granted') return true;
    if (state === 'denied') return false;
    return requestMicrophoneAccess();
  }

  /** Начинает запись. Предыдущий захват, если он остался, отпускается. */
  async start(): Promise<void> {
    this.#assertNotDisposed();
    this.#releaseCapture();
    this.#frames = [];
    // Имя складывается в момент старта — как в Dart, где путь
    // '<tmp>/${DateTime.now().millisecondsSinceEpoch}_audio.wav'
    // передаётся в `start()`.
    this.#fileName = buildFileName();
    const generation = this.#nextGeneration();

    const stream = await openMicrophoneStream();
    let session: CaptureSession;
    try {
      session = await this.#attachCapture(stream, generation);
    } catch (error) {
      releaseStream(stream);
      this.#fileName = null;
      throw error;
    }

    if (generation !== this.#generation) {
      // `stop()` или `dispose()` успели пройти, пока открывался микрофон.
      // Захват отпускаем сразу, иначе индикатор записи во вкладке останется
      // гореть до перезагрузки киоска.
      releaseCapture(session);
      return;
    }
    this.#capture = session;
  }

  /**
   * Останавливает запись и отдаёт готовый WAV.
   *
   * `null` — записать ничего не успели (посетитель отпустил кнопку раньше,
   * чем пришёл первый кадр).
   */
  async stop(): Promise<File | null> {
    const capture = this.#capture;
    const frames = this.#frames;
    const fileName = this.#fileName;
    this.#nextGeneration();
    this.#frames = [];
    this.#fileName = null;
    if (capture === null) return null;

    const sampleRate = capture.context.sampleRate;
    // Дорожки отпускаем до кодирования, чтобы индикатор записи во вкладке
    // погас сразу, а не после упаковки файла.
    this.#releaseCapture();
    return buildWavFile(frames, sampleRate, fileName);
  }

  /** Освобождение: соответствует `_recorder?.dispose()` в контроллере. */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#nextGeneration();
    this.#releaseCapture();
    this.#frames = [];
    this.#fileName = null;
  }

  async #attachCapture(
    stream: MediaStream,
    generation: number,
  ): Promise<CaptureSession> {
    const context = createAudioContext();
    try {
      // Политика автозапуска: контекст может родиться приостановленным.
      if (context.state === 'suspended') await context.resume();
      const source = context.createMediaStreamSource(stream);
      const node = await createCaptureNode(context, (frame) => {
        this.#onFrame(generation, frame);
      });
      source.connect(node);
      // Узел обязан быть подключён к выходу, иначе браузер не станет его
      // обсчитывать. В выходной буфер мы ничего не пишем — из динамиков
      // тишина, эха собственного микрофона нет.
      node.connect(context.destination);
      return { stream, context, source, node };
    } catch (error) {
      closeContext(context);
      throw error;
    }
  }

  /**
   * Накопление кадров: массив каждый раз новый, старый не меняется.
   * Кадры отменённого захвата отбрасываются по номеру попытки.
   */
  #onFrame(generation: number, frame: Float32Array): void {
    if (generation !== this.#generation || frame.length === 0) return;
    this.#frames = [...this.#frames, frame];
  }

  #releaseCapture(): void {
    const capture = this.#capture;
    if (capture === null) return;
    this.#capture = null;
    releaseCapture(capture);
  }

  #nextGeneration(): number {
    this.#generation += 1;
    return this.#generation;
  }

  #assertNotDisposed(): void {
    if (this.#disposed) {
      throw new Error('AudioRecorder: запись после dispose()');
    }
  }
}

/**
 * Замена `openAppSettings()` из permission_handler.
 *
 * Страница не может открыть настройки сайта: разрешение микрофона
 * снимается только вручную — через значок в адресной строке. Поэтому здесь
 * ничего не происходит, а вызывающему остаётся показать тот же тост
 * `error_no_permission_access`, что и во Flutter.
 */
export function openMicrophoneSettingsHint(): void {
  // Намеренно пусто: веб-эквивалента нет.
}

/**
 * Состояние разрешения через Permissions API.
 *
 * `null` — API нет (Safari до 16) или он не знает дескриптор `microphone`
 * (Firefox): тогда ответ даст только getUserMedia.
 */
async function queryMicrophonePermission(): Promise<PermissionState | null> {
  const permissions: Permissions | undefined = navigator.permissions;
  if (permissions === undefined) return null;
  try {
    const status = await permissions.query({ name: 'microphone' });
    return status.state;
  } catch {
    // Дескриптор 'microphone' знают не все браузеры — тогда query()
    // бросает TypeError, и это не ошибка устройства, а отсутствие ответа.
    return null;
  }
}

/**
 * Спрашивает доступ у посетителя и сразу отпускает дорожки — поток откроет
 * `start()`. Отказ — это `false`; любая другая ошибка уходит наверх, чтобы
 * вызывающий показал сбой устройства (в Dart —
 * `AppFailure.device(FailureOp.permission, …)`).
 */
async function requestMicrophoneAccess(): Promise<boolean> {
  try {
    const stream = await openMicrophoneStream();
    releaseStream(stream);
    return true;
  } catch (error) {
    if (isPermissionDenied(error)) return false;
    throw error;
  }
}

async function openMicrophoneStream(): Promise<MediaStream> {
  const devices: MediaDevices | undefined = navigator.mediaDevices;
  if (devices === undefined) {
    // getUserMedia есть только в защищённом контексте: https или localhost.
    throw new Error('Микрофон недоступен: страница открыта не по https');
  }
  return devices.getUserMedia(MEDIA_CONSTRAINTS);
}

function isPermissionDenied(error: unknown): boolean {
  return error instanceof DOMException && error.name === PERMISSION_DENIED_ERROR;
}

/**
 * Контекст на 16 кГц. Часть браузеров молча отдаёт свою частоту, часть
 * бросает на неподдерживаемое значение — тогда берём контекст по умолчанию,
 * а кадры пересчитываются в 16 кГц при остановке.
 */
function createAudioContext(): AudioContext {
  try {
    return new AudioContext({ sampleRate: RECORDER_SAMPLE_RATE });
  } catch (error) {
    if (!(error instanceof DOMException)) throw error;
    return new AudioContext();
  }
}

/** Есть ли в браузере AudioWorklet: и сам реестр, и конструктор узла. */
function supportsAudioWorklet(context: AudioContext): boolean {
  const worklet: AudioWorklet | undefined = context.audioWorklet;
  return worklet !== undefined && typeof AudioWorkletNode !== 'undefined';
}

async function createCaptureNode(
  context: AudioContext,
  onFrame: FrameHandler,
): Promise<AudioNode> {
  if (supportsAudioWorklet(context)) {
    try {
      return await createWorkletNode(context, onFrame);
    } catch (error) {
      // Модуль ворклета может не загрузиться из-за политики безопасности,
      // запрещающей blob:-скрипты. Запись важнее — уходим на запасной путь,
      // но причину не прячем.
      console.warn(
        'AudioRecorder: AudioWorklet не загрузился, переходим на ScriptProcessorNode',
        error,
      );
    }
  }
  return createScriptProcessorNode(context, onFrame);
}

async function createWorkletNode(
  context: AudioContext,
  onFrame: FrameHandler,
): Promise<AudioWorkletNode> {
  await loadRecorderWorklet(context);
  const node = new AudioWorkletNode(context, RECORDER_WORKLET_NAME, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [RECORDER_CHANNELS],
    // Явное сведение в моно: если устройство всё-таки отдаст стерео,
    // процессор всё равно получит один канал.
    channelCount: RECORDER_CHANNELS,
    channelCountMode: 'explicit',
  });
  node.port.onmessage = (event: MessageEvent<Float32Array>) => {
    onFrame(event.data);
  };
  return node;
}

/**
 * Запасной путь для браузеров без `AudioWorklet` (Safari до 14.1).
 * `ScriptProcessorNode` объявлен устаревшим и считает кадры в основном
 * потоке, но другого способа получить сырой PCM там нет.
 */
function createScriptProcessorNode(
  context: AudioContext,
  onFrame: FrameHandler,
): ScriptProcessorNode {
  const node = context.createScriptProcessor(
    SCRIPT_PROCESSOR_BUFFER_SIZE,
    RECORDER_CHANNELS,
    RECORDER_CHANNELS,
  );
  node.onaudioprocess = (event) => {
    // Буфер переиспользуется движком — копируем кадр.
    onFrame(new Float32Array(event.inputBuffer.getChannelData(0)));
  };
  return node;
}

function detachNode(node: AudioNode): void {
  // `AudioWorkletNode` может отсутствовать в старых браузерах, поэтому
  // сначала проверяем сам конструктор.
  const hasWorkletNode = typeof AudioWorkletNode !== 'undefined';
  /* eslint-disable no-param-reassign -- у узлов Web Audio обработчик
     отцепляется только присваиванием null; сам узел не подменяется */
  if (hasWorkletNode && node instanceof AudioWorkletNode) {
    node.port.onmessage = null;
    node.port.close();
  } else if ('onaudioprocess' in node) {
    (node as ScriptProcessorNode).onaudioprocess = null;
  }
  /* eslint-enable no-param-reassign */
  node.disconnect();
}

/** Разбирает захват целиком: узлы, дорожки микрофона и контекст. */
function releaseCapture(capture: CaptureSession): void {
  detachNode(capture.node);
  capture.source.disconnect();
  releaseStream(capture.stream);
  closeContext(capture.context);
}

/** Отпускает дорожки — индикатор записи во вкладке гаснет. */
function releaseStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

/**
 * Закрытие контекста на пути освобождения. Ошибку здесь нельзя терять
 * молча, но и падать из-за неё не стоит — она только скроет настоящую
 * причину, поэтому пишем предупреждение.
 */
function closeContext(context: AudioContext): void {
  void context.close().catch((error: unknown) => {
    console.warn('AudioRecorder: не удалось закрыть AudioContext', error);
  });
}

/** '<миллисекунды>_audio.wav' — то же имя, что уходило в multipart из Dart. */
function buildFileName(): string {
  return `${Date.now()}_audio.wav`;
}

/** Склейка кадров, приведение к 16 кГц и упаковка в WAV. */
function buildWavFile(
  frames: readonly Float32Array[],
  sampleRate: number,
  fileName: string | null,
): File | null {
  const samples = concatSamples(frames);
  if (samples.length === 0) return null;
  const resampled = resampleLinear(samples, sampleRate, RECORDER_SAMPLE_RATE);
  const wav = encodeWav(resampled, RECORDER_SAMPLE_RATE);
  return new File([wav], fileName ?? buildFileName(), { type: WAV_MIME_TYPE });
}
